(function () {
  // Guard against the script being injected more than once on a page
  // (the snippet renders this <script> tag for every carousel instance).
  if (customElements.get('core-carousel')) {
    return;
  }

class CoreCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.track = this.querySelector('.core-carousel__track');
    this.isSlider = this.dataset.isSlider; // 'true', 'false', or 'mobile'
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.resizeHandler = this.handleResize.bind(this);
    
    // Only initialize if configured as a slider
    if (this.isSlider === 'true' && this.track) {
      this.setupObserver();
    } else if (this.isSlider === 'mobile' && this.track) {
      if (this.mediaQuery.matches) {
        this.setupObserver();
      }
      this.mediaQuery.addEventListener('change', this.resizeHandler);
    }
  }

  handleResize(e) {
    if (e.matches) {
      // Transitioned to mobile
      if (!this.swiperInstance && this.isSlider === 'mobile') {
        this.setupObserver();
      }
    } else {
      // Transitioned to desktop
      if (this.swiperInstance && this.isSlider === 'mobile') {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
        // Clean up DOM modifications Swiper makes to the container
        this.classList.remove('swiper', 'swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');
        if (this.track) {
          this.track.classList.remove('swiper-wrapper');
          this.track.removeAttribute('style'); // Remove inline transform and transition
        }
        const slides = this.querySelectorAll('.core-carousel__slide');
        slides.forEach(slide => {
          slide.classList.remove('swiper-slide', 'swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev');
          slide.removeAttribute('style'); // Remove inline width
        });
      }
    }
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadSwiperAssets();
          this.observer.unobserve(this);
        }
      });
    }, options);

    this.observer.observe(this);
  }

  loadSwiperAssets() {
    // Schedule initialization for idle time to avoid blocking main thread (TBT)
    const initFn = () => this.injectAssetsAndInit();
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initFn, { timeout: 1000 });
    } else {
      setTimeout(initFn, 200);
    }
  }

  injectAssetsAndInit() {
    const jsUrl = this.dataset.swiperJs;
    const cssUrl = this.dataset.swiperCss;

    if (!jsUrl || !cssUrl) {
      console.error('CoreCarousel: Missing Swiper asset URLs');
      return;
    }

    // Load CSS
    if (!document.querySelector(`link[href="${cssUrl}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
    }

    // Load JS
    if (window.Swiper) {
      this.initSwiper();
    } else if (!document.querySelector(`script[src="${jsUrl}"]`)) {
      const script = document.createElement('script');
      script.src = jsUrl;
      script.async = true;
      script.onload = () => this.initSwiper();
      document.head.appendChild(script);
    } else {
      // Script is loading, wait for it
      const checkInterval = setInterval(() => {
        if (window.Swiper) {
          clearInterval(checkInterval);
          this.initSwiper();
        }
      }, 50);
    }
  }

  initSwiper() {
    // Safety check for mobile mode if resize happened during script load
    if (this.isSlider === 'mobile' && !this.mediaQuery.matches) return;

    // Add required Swiper classes to DOM structure
    this.classList.add('swiper');
    this.track.classList.add('swiper-wrapper');
    const slides = this.querySelectorAll('.core-carousel__slide');
    slides.forEach(slide => slide.classList.add('swiper-slide'));

    const offsetMobileRaw = this.dataset.offsetMobile || "0";
    const offsetMobile = parseFloat(offsetMobileRaw.replace(',', '.')) * 10 || 0;

    const offsetDesktopRaw = this.dataset.offsetDesktop || "0";
    const offsetDesktop = parseFloat(offsetDesktopRaw.replace(',', '.')) * 10 || 0;

    const config = {
      slidesPerView: parseFloat(this.dataset.slidesMobile) || 1,
      spaceBetween: parseFloat(this.dataset.gap) * 10 || 16, // Assuming gap is in rem and 1rem=10px
      slidesOffsetBefore: offsetMobile,
      slidesOffsetAfter: offsetMobile,
      loop: this.dataset.loop === 'true',
      speed: parseInt(this.dataset.speed) || 300,
      breakpoints: {
        750: {
          slidesPerView: parseFloat(this.dataset.slidesTablet) || 2,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0
        },
        990: {
          slidesPerView: parseFloat(this.dataset.slidesDesktop) || 3,
          slidesOffsetBefore: offsetDesktop,
          slidesOffsetAfter: offsetDesktop
        }
      }
    };

    // Optional modules
    if (this.dataset.navigation === 'true') {
      config.navigation = {
        nextEl: this.querySelector('.core-carousel-button--next'),
        prevEl: this.querySelector('.core-carousel-button--prev'),
      };
    }

    if (this.dataset.pagination === 'true') {
      config.pagination = {
        el: this.querySelector('.core-carousel-pagination'),
        type: 'bullets',
        clickable: true,
      };
    }

    if (this.dataset.autoplay === 'true') {
      config.autoplay = {
        delay: parseInt(this.dataset.autoplayDelay) || 3000,
        disableOnInteraction: false,
      };
    }

    if (this.dataset.thumbsSlider) {
      const thumbsEl = document.querySelector(this.dataset.thumbsSlider);
      // Ensure the thumbs slider is initialized or wait for it
      if (thumbsEl && thumbsEl.swiperInstance) {
        config.thumbs = {
          swiper: thumbsEl.swiperInstance
        };
      } else if (thumbsEl) {
        // If the thumbs element exists but isn't initialized yet, we can pass the element itself
        // Note: For Swiper web components, we need the swiper instance. But since we use normal swiper
        // on the custom element, passing the element works if it initializes in time, or we might need
        // to wait. For simplicity, we just pass the element. Swiper handles HTML elements for thumbs.
        config.thumbs = {
          swiper: thumbsEl
        };
      }
    }

    this.swiperInstance = new window.Swiper(this, config);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.swiperInstance && !this.swiperInstance.destroyed) {
      this.swiperInstance.destroy(true, true);
    }
    if (this.isSlider === 'mobile') {
      this.mediaQuery.removeEventListener('change', this.resizeHandler);
    }
  }
}

if (!customElements.get('core-carousel')) {
  customElements.define('core-carousel', CoreCarousel);
}

// Support Shopify Editor
document.addEventListener('shopify:section:load', (e) => {
  const sliders = e.target.querySelectorAll('core-carousel');
  sliders.forEach(slider => {
    if (slider.swiperInstance) {
      slider.swiperInstance.update();
    } else if (slider.isSlider === 'true' || (slider.isSlider === 'mobile' && window.innerWidth < 750)) {
      slider.loadSwiperAssets();
    }
  });
});

})();
